import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  Alert,
  Button,
  Divider,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import proxy from "../../api/axios";

const ACCENT = "#137274";

const THINKING_PHRASES = [
  "Reading the problem…",
  "Thinking it through…",
  "Crafting your hint…",
];

const aiPulseKeyframes = {
  "0%, 100%": { opacity: 0.4, transform: "scale(0.88) rotate(0deg)" },
  "50%": { opacity: 1, transform: "scale(1.08) rotate(18deg)" },
};

const AiThinkingIndicator = ({ compact }) => {
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setPhraseIndex((i) => (i + 1) % THINKING_PHRASES.length);
    }, 1300);
    return () => clearInterval(id);
  }, []);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <AutoAwesomeIcon
        sx={{
          fontSize: compact ? 16 : 18,
          color: ACCENT,
          flexShrink: 0,
          animation: "ai-hint-pulse 1.1s ease-in-out infinite",
          "@keyframes ai-hint-pulse": aiPulseKeyframes,
        }}
      />
      {!compact && (
        <Typography
          sx={{ fontSize: "0.8rem", color: "text.secondary", fontStyle: "italic" }}
        >
          {THINKING_PHRASES[phraseIndex]}
        </Typography>
      )}
    </Box>
  );
};

const HINT_LEVELS = [
  {
    key: "basic",
    label: "Basic",
    color: "#1a7f37",
    desc: "A gentle nudge in the right direction",
  },
  {
    key: "medium",
    label: "Medium",
    color: "#d97706",
    desc: "A clearer approach, short of the full solution",
  },
  {
    key: "advanced",
    label: "Advanced",
    color: "#cf222e",
    desc: "A detailed strategy and key considerations",
  },
];

const ProblemHint = ({ problemDetails }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [hints, setHints] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});
  const [open, setOpen] = useState({});

  const fetchHint = async (level) => {
    setLoading((prev) => ({ ...prev, [level]: true }));
    setError((prev) => ({ ...prev, [level]: null }));
    try {
      const response = await proxy.post("/problems/hints", {
        ...problemDetails,
        hintType: level,
      });
      setHints((prev) => ({ ...prev, [level]: response.data.hint }));
    } catch (err) {
      setError((prev) => ({
        ...prev,
        [level]: "Failed to fetch hint. Please try again.",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [level]: false }));
    }
  };

  const toggle = (level) => {
    const willOpen = !open[level];
    setOpen((prev) => ({ ...prev, [level]: willOpen }));
    if (willOpen && !hints[level] && !loading[level]) fetchHint(level);
  };

  const borderColor = isDark ? "#30363d" : "rgba(15,60,70,0.08)";
  const cardBg = isDark ? "#010409" : "#fff";

  return (
    <Box data-tour="problem-hint" sx={{ mt: 1 }}>
      <Divider sx={{ my: 2.5 }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        <LightbulbOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
        <Typography
          sx={{ fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.02em" }}
        >
          AI Hints
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", fontSize: "0.78rem", mb: 1.5 }}
      >
        Stuck? Reveal hints one level at a time — each gives away a little
        more.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {HINT_LEVELS.map(({ key, label, color, desc }) => {
          const isOpen = !!open[key];
          return (
            <Box
              key={key}
              sx={{
                border: "1px solid",
                borderColor: isOpen ? alpha(color, 0.5) : borderColor,
                borderRadius: 1.5,
                overflow: "hidden",
                bgcolor: cardBg,
                transition: "border-color 0.15s ease",
              }}
            >
              <Box
                onClick={() => toggle(key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": {
                    bgcolor: isDark ? alpha("#fff", 0.03) : alpha("#000", 0.02),
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.6,
                      px: 1,
                      py: 0.35,
                      borderRadius: 1.25,
                      bgcolor: `${color}18`,
                      border: `1px solid ${color}40`,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        bgcolor: color,
                      }}
                    />
                    <Typography
                      sx={{ fontWeight: 700, color, fontSize: "0.72rem" }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  {!isOpen && (
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "0.78rem",
                        color: "text.secondary",
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      {desc}
                    </Typography>
                  )}
                </Box>
                {loading[key] ? (
                  <AiThinkingIndicator compact />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      color: "text.secondary",
                      flexShrink: 0,
                      transform: isOpen ? "rotate(180deg)" : "none",
                      transition: "transform 0.15s ease",
                    }}
                  />
                )}
              </Box>
              <Collapse in={isOpen}>
                <Box
                  sx={{
                    px: 1.5,
                    pb: 1.5,
                    borderTop: "1px solid",
                    borderColor,
                  }}
                >
                  {loading[key] ? (
                    <Box sx={{ pt: 1.25 }}>
                      <AiThinkingIndicator />
                    </Box>
                  ) : error[key] ? (
                    <Alert
                      severity="error"
                      sx={{ mt: 1.25, fontSize: "0.78rem" }}
                      action={
                        <Button
                          size="small"
                          onClick={() => fetchHint(key)}
                          startIcon={<RefreshIcon fontSize="small" />}
                        >
                          Retry
                        </Button>
                      }
                    >
                      {error[key]}
                    </Alert>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        pt: 1.25,
                        color: "text.primary",
                      }}
                    >
                      {hints[key]}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProblemHint;
